/**
 * Lead Supply referenced original D/T retrieval, upload, and review actions.
 */
import { formatInteger } from "./format.js";
import { authenticatedFetch, DashboardAuthRequiredError } from "./dashboard-auth.js";
import {
  leadFactoryOcrUrl,
  leadFactoryReferencedDeedDirectUrl,
  leadFactoryReferencedDeedQueueUrl,
  leadFactoryReferencedDeedRetrievalReviewUrl,
  leadFactoryReferencedDeedUploadUrl,
  leadFactoryReferencedDeedUrl
} from "./dashboard-urls.js";

export type DashboardActionHost = {
  refresh: () => void;
  onAuthRequired: (message: string) => void;
};

export interface ReferencedDeedCaptureInput {
  referencedDeedBook?: string;
  referencedDeedPage?: string;
  sourcePage?: string;
  evidenceExcerpt?: string;
}

export async function submitLeadSupplyReferencedDeedRetrieval(
  sourceDocumentId: string,
  leadId: string | undefined,
  statusElement: HTMLElement,
  capture: ReferencedDeedCaptureInput | undefined,
  actions: DashboardActionHost
): Promise<void> {
  statusElement.textContent = `${sourceDocumentId}: preparing referenced D/T retrieval...`;
  try {
    const capturePayload = capture && (capture.referencedDeedBook || capture.referencedDeedPage || capture.sourcePage || capture.evidenceExcerpt)
      ? {
          referencedDeedBook: capture.referencedDeedBook?.trim() || undefined,
          referencedDeedPage: capture.referencedDeedPage?.trim() || undefined,
          sourcePage: capture.sourcePage?.trim() || undefined,
          evidenceExcerpt: capture.evidenceExcerpt?.trim() || undefined
        }
      : {};
    const response = await authenticatedFetch(leadFactoryReferencedDeedUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceDocumentId,
        leadId: leadId?.trim() || undefined,
        limit: 1,
        ...capturePayload
      })
    });
    const body = (await response.json()) as { error?: string; referenceFactsCaptured?: number; retrievalsPrepared?: number; retrievalsBlocked?: number };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: captured ${formatInteger(body.referenceFactsCaptured ?? 0)} reference facts; prepared ${formatInteger(body.retrievalsPrepared ?? 0)} referenced D/T retrievals; ${formatInteger(body.retrievalsBlocked ?? 0)} blocked. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Referenced D/T retrieval failed.";
  }
}

export async function submitLeadSupplyReferencedOriginalOcrQueue(sourceDocumentId: string, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  statusElement.textContent = `${sourceDocumentId}: queueing attached original D/T OCR...`;
  try {
    const response = await authenticatedFetch(leadFactoryOcrUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sourceDocumentId, limit: 1 })
    });
    const body = (await response.json()) as {
      error?: string;
      sourceDocumentsScanned?: number;
      ocrTasksQueued?: number;
      artifactsQueued?: number;
      jobStatusCounts?: Record<string, number>;
    };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    const queued = body.ocrTasksQueued ?? 0;
    const scanned = body.sourceDocumentsScanned ?? 0;
    statusElement.textContent = queued > 0
      ? `${sourceDocumentId}: queued OCR for attached original D/T. Refreshing...`
      : `${sourceDocumentId}: no OCR task queued from ${formatInteger(scanned)} source document(s); it may already be queued or missing source evidence. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Attached original D/T OCR queueing failed.";
  }
}

export async function submitLeadSupplyReferencedDeedUpload(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const sourceDocumentId = form.dataset.sourceDocumentId;
  if (!sourceDocumentId) return;
  const file = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-file]")?.files?.[0];
  if (!file) {
    statusElement.textContent = `${sourceDocumentId}: choose the original D/T PDF before uploading.`;
    return;
  }
  const filedDate = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-filed-date]")?.value.trim() || "";
  if (!filedDate) {
    statusElement.textContent = `${sourceDocumentId}: enter the original D/T filed date before uploading.`;
    return;
  }
  const upload = new FormData();
  upload.set("sourceDocumentId", sourceDocumentId);
  upload.set("leadId", form.dataset.leadId || "");
  if (form.hasAttribute("data-lead-supply-manual-evidence-status-form") && form.dataset.taskId) {
    upload.set("manualEvidenceTaskId", form.dataset.taskId);
  }
  upload.set("filedDate", filedDate);
  upload.set("sourceUrl", form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-source-url]")?.value.trim() || "");
  upload.set("note", "Referenced original Deed of Trust uploaded from Lead Supply.");
  upload.set("file", file);
  statusElement.textContent = `${sourceDocumentId}: uploading referenced original D/T PDF...`;
  try {
    const response = await authenticatedFetch(leadFactoryReferencedDeedUploadUrl, {
      method: "POST",
      body: upload
    });
    const body = (await response.json()) as { error?: string; referencedSourceDocumentId?: string; status?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: uploaded and linked original D/T ${body.referencedSourceDocumentId ?? ""}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Referenced D/T upload failed.";
  }
}

export async function submitLeadSupplyReferencedDeedDirectFetch(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const sourceDocumentId = form.dataset.sourceDocumentId;
  if (!sourceDocumentId) return;
  statusElement.textContent = `${sourceDocumentId}: fetching referenced original D/T from county records...`;
  try {
    const response = await authenticatedFetch(leadFactoryReferencedDeedDirectUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceDocumentId,
        leadId: form.dataset.leadId || undefined,
        note: "Referenced original Deed of Trust retrieved directly from Lead Supply."
      })
    });
    const body = (await response.json()) as { error?: string; referencedSourceDocumentId?: string; status?: string; message?: string };
    if (!response.ok) throw new Error(body.message ?? body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: fetched and linked original D/T ${body.referencedSourceDocumentId ?? ""}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Referenced D/T direct fetch failed.";
  }
}

export async function submitLeadSupplyReferencedDeedRunnerQueue(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const sourceDocumentId = form.dataset.sourceDocumentId;
  if (!sourceDocumentId) return;
  statusElement.textContent = `${sourceDocumentId}: queueing referenced original D/T runner retrieval...`;
  try {
    const response = await authenticatedFetch(leadFactoryReferencedDeedQueueUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceDocumentId,
        leadId: form.dataset.leadId || undefined,
        priority: "high",
        note: "Referenced original Deed of Trust queued from Lead Supply for runner-backed retrieval."
      })
    });
    const body = (await response.json()) as { error?: string; job?: { id?: string; retrievalProfile?: string }; message?: string };
    if (!response.ok) throw new Error(body.message ?? body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: queued ${body.job?.retrievalProfile ?? "runner"} retrieval ${body.job?.id ?? ""}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Referenced D/T runner queue failed.";
  }
}

export async function submitLeadSupplyReferencedDeedRetrievalReview(form: HTMLFormElement, statusElement: HTMLElement, actions: DashboardActionHost): Promise<void> {
  const sourceDocumentId = form.dataset.sourceDocumentId;
  if (!sourceDocumentId) return;
  const referencedDeedBook = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-book]")?.value.trim() || "";
  const referencedDeedPage = form.querySelector<HTMLInputElement>("[data-lead-supply-referenced-dt-page]")?.value.trim() || "";
  const referencedDeedBookPage = referencedDeedBook && referencedDeedPage ? `${referencedDeedBook}/${referencedDeedPage}` : "";
  const reason = referencedDeedBookPage
    ? `County lookup was reviewed and manual source evidence is needed for ${referencedDeedBookPage}.`
    : "County lookup was reviewed and manual source evidence is needed.";
  statusElement.textContent = `${sourceDocumentId}: recording failed referenced D/T review...`;
  try {
    const response = await authenticatedFetch(leadFactoryReferencedDeedRetrievalReviewUrl, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sourceDocumentId,
        leadId: form.dataset.leadId || undefined,
        referencedDeedBook: referencedDeedBook || undefined,
        referencedDeedPage: referencedDeedPage || undefined,
        referencedDeedBookPage: referencedDeedBookPage || undefined,
        outcome: "manual_source_needed",
        reason,
        note: "Failed referenced original D/T lookup reviewed from Lead Supply."
      })
    });
    const body = (await response.json()) as { error?: string; outcome?: string; jobId?: string };
    if (!response.ok) throw new Error(body.error ?? `Request failed with ${response.status}`);
    statusElement.textContent = `${sourceDocumentId}: recorded ${body.outcome ?? "manual source needed"} review ${body.jobId ?? ""}. Refreshing...`;
    setTimeout(() => actions.refresh(), 650);
  } catch (error) {
    if (error instanceof DashboardAuthRequiredError) {
      actions.onAuthRequired(error.message);
      return;
    }
    statusElement.textContent = error instanceof Error ? error.message : "Referenced D/T review failed.";
  }
}

